import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DescriptionIcon from "@mui/icons-material/Description";
import { useState } from "react";
import OnboardingUpload from "../components/OnboardingUpload";
import OnboardingDetail from "../components/OnboardingDetail";
import OnboardingFileDetail from "../components/OnboardingFileDetail";
import PreAuthorizationUpload from "../components/PreAuthorizationUpload";
import PreauthorizedDetails from "../components/PreauthorizedDetails";

async function fetchBulkPreauthorizedStandalone() {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/onboarding/preauthorized`
  );
  return response;
}

async function fetchEmployerAttributes() {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/employer/attributes`
  );
  return response;
}

export default function BulkPreauthorizeStandalone() {
  const [onboardingFileDetail, setOnboardingFileDetail] = useState();
  const [openUploadDlg, setOpenUploadDlg] = useState(false);
  const [selectedEmployer, setSelectedEmployer] = useState("");
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const columns = [
    { field: "id", headerName: "id", width: 10, hide: true },
    { field: "employerName", headerName: "Company", width: 200 },
    { field: "filename", headerName: "Filename", width: 350 },
    {
      field: "processStatus",
      headerName: "Status",
      width: 150,
      editable: false,
    },
    {
      field: "numOfRecords",
      headerName: "Number of records",
      width: 150,
      editable: false,
    },
    {
      field: "viewDetails",
      headerName: "View Details",
      width: 100,
      renderCell: (params) => (
        <div key={"action." + params.id}>
          <IconButton
            key={"view." + params.id}
            onClick={() => {
              setOnboardingFileDetail({ ...params.row });
            }}
          >
            <VisibilityIcon />
          </IconButton>
        </div>
      ),
    },
  ];

  const { data, isLoading } = useQuery(
    ["bulk_preauthorization"],
    fetchBulkPreauthorizedStandalone
  );

  const { data: attributesData } = useQuery(
    ["employerAttributes"],
    fetchEmployerAttributes
  );

  const padStandaloneEmployers = (attributesData?.data || [])
    .filter(
      (attr) =>
        attr.attributeName === "IS_PAD_STANDALONE" && attr.value === "true"
    )
    .map((attr) => ({ name: attr.employerName, idEmployer: attr.idEmployer }))
    .filter(
      (item, index, self) =>
        self.findIndex((e) => e.idEmployer === item.idEmployer) === index
    );

  const padEmployerIdToName = Object.fromEntries(
    padStandaloneEmployers.map((e) => [e.idEmployer, e.name])
  );

  const filteredRows = selectedEmployer
    ? (data?.data || []).filter(
        (row) => row.employerName === padEmployerIdToName[selectedEmployer]
      )
    : data?.data || [];

  const queryClient = useQueryClient();

  const uploadDialog = () => {
    return (
      <Dialog open={openUploadDlg} fullWidth maxWidth="lg" onClose={() => {}}>
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <UploadFileIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              Upload File to Preauthorize members for PAD Standalone
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setOpenUploadDlg(false);
              queryClient.invalidateQueries({
                queryKey: ["bulk_preauthorization"],
              });
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <PreAuthorizationUpload
            setOpen={setOpenUploadDlg}
            employerId={selectedEmployer}
          />
        </DialogContent>
      </Dialog>
    );
  };

  const onboardingFileDetailDialog = (event) => {
    return (
      <Dialog
        open={onboardingFileDetail !== "undefined"}
        fullWidth
        onClose={() => {}}
        maxWidth="xl"
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DescriptionIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              Preauthorized File Detail
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setOnboardingFileDetail();
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <PreauthorizedDetails onboardingInfo={onboardingFileDetail} />
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {isLoading ? (
        ""
      ) : (
        <Grid container p={0} m={0} spacing={0}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Typography variant="h5">
                Census Update for Pay Any-Day (Payroll Deduction)
              </Typography>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel id="pad-employer-label">Company</InputLabel>
                <Select
                  labelId="pad-employer-label"
                  value={selectedEmployer}
                  label="Employer"
                  onChange={(e) => setSelectedEmployer(e.target.value)}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {padStandaloneEmployers.map((employer) => (
                    <MenuItem
                      key={employer.idEmployer}
                      value={employer.idEmployer}
                    >
                      {employer.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setOpenUploadDlg(true)}
              >
                Upload File
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sx={{ width: "100%", height: "80vh" }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              getRowHeight={() => 25}
              density="compact"
              disableDensitySelector
              disableColumnSelector
              disableColumnFilter
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
          </Grid>
        </Grid>
      )}
      {onboardingFileDetail ? onboardingFileDetailDialog() : ""}
      {openUploadDlg ? uploadDialog() : ""}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setFeedback({ ...feedback, open: false })}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
